import type { ChauffeurPage } from "../slug-chauffeur";

import heroImage from "@/assets/images/chauffeur/transfert/chauffeur-transfert.png";
import { cityPriceFaq } from "./cityPricing";

const hyeres: ChauffeurPage = {
  slug: "hyeres",
  title: "Chauffeur privé & VTC à Hyères",
  subtitle:
    "Chauffeur privé à Hyères : ville des palmiers, presqu'île de Giens, départ des îles d'Or et aéroport de Toulon-Hyères à 15 min.",
  keywords: [
    "chauffeur privé hyères",
    "vtc hyères",
    "transfert aéroport toulon-hyères",
    "chauffeur presqu'île de giens",
  ],
  hero: {
    title: "Chauffeur privé & VTC à Hyères",
    subtitle:
      "La ville des palmiers, la presqu'île de Giens et le départ des îles d'Or : un chauffeur à deux pas de l'aéroport.",
    keywords: [
      "VTC Hyères",
      "chauffeur presqu'île de Giens",
      "La Tour Fondue Porquerolles",
      "aéroport de Toulon-Hyères",
    ],
    imageSrc: heroImage.src,
    imageAlt: "Chauffeur privé & VTC à Hyères",
    badges: [
      "Aéroport Toulon-Hyères ~15 min",
      "Départ îles d'Or",
      "Chauffeurs FR/EN",
    ],
  },
  benefits: {
    heading: "Pourquoi un chauffeur à Hyères",
    items: [
      {
        title: "Aéroport à 15 min",
        description:
          "L'aéroport de Toulon-Hyères est sur la commune, à environ 15 min ; accueil sans attente et suivi de vol.",
      },
      {
        title: "Départ des îles d'Or",
        description:
          "Liaison avec La Tour Fondue pour Porquerolles, et le port pour Port-Cros et Le Levant, bagages portés.",
      },
      {
        title: "Presqu'île de Giens & salins",
        description:
          "Le double tombolo, les salins des Pesquiers et les plages de la presqu'île desservis sans souci de parking.",
      },
      {
        title: "Service noté 5,0/5",
        description:
          "Chauffeurs ponctuels et discrets, basés sur la Côte d'Azur, notés 5,0/5 sur 38 avis Google.",
      },
    ],
  },
  services: {
    heading: "Nos services de chauffeur à Hyères",
    services: [
      {
        title: "Transfert aéroport de Toulon-Hyères",
        text: "L'aéroport le plus proche, à environ 15 min du centre, avec suivi de vol et accueil personnalisé.",
      },
      {
        title: "Transfert aéroport de Nice",
        text: "Pour les vols internationaux, liaison vers Nice Côte d'Azur en près de 1h30, van premium sur demande.",
      },
      {
        title: "Départ îles & La Tour Fondue",
        text: "Dépose à l'embarcadère de Porquerolles à la Tour Fondue et au port pour Port-Cros et Le Levant.",
      },
      {
        title: "Gare TGV de Toulon",
        text: "Transfert vers la gare de Toulon pour les TGV Paris, en complément de la gare d'Hyères.",
      },
      {
        title: "Mise à disposition & longue distance",
        text: "Chauffeur à la journée pour la presqu'île, ou trajets vers Saint-Tropez, Cannes, Nice et l'Italie.",
      },
    ],
  },
  locations: {
    heading: "Lieux & points de prise en charge",
    items: [
      {
        name: "Vieille ville d'Hyères",
        description: "Centre médiéval, place Massillon et parc Saint-Bernard.",
      },
      {
        name: "Villa Noailles",
        description: "Villa moderniste et centre d'art, au-dessus de la ville.",
      },
      {
        name: "Presqu'île de Giens",
        description: "Double tombolo, plages et route du sel.",
      },
      {
        name: "La Tour Fondue",
        description: "Embarcadère pour Porquerolles, à la pointe de Giens.",
      },
      {
        name: "Salins des Pesquiers",
        description: "Anciens salins et étangs à flamants roses.",
      },
      {
        name: "Aéroport de Toulon-Hyères",
        description: "Sur la commune, à environ 15 min du centre.",
      },
    ],
  },
  generic: {
    heading: "Hyères, la ville des palmiers face aux îles d'Or",
    subheading:
      "Une ville étendue entre vieille cité, presqu'île et aéroport, où un chauffeur simplifie chaque étape.",
    paragraphs: [
      "Hyères, dite « les Palmiers », s'étire de sa vieille ville médiévale jusqu'à la mer et à la presqu'île de Giens. Les distances internes sont réelles : entre la place Massillon, la Villa Noailles perchée et les plages du double tombolo, un chauffeur privé vous fait gagner du temps et vous dépose au plus près, salins et route du sel compris.",
      "La commune abrite l'aéroport de Toulon-Hyères, à quinze minutes seulement du centre : c'est le point d'entrée le plus rapide de tout ce littoral. Pour les vols internationaux, Nice Côte d'Azur reste à près de 1h30, et la gare TGV de Toulon, en complément de la gare d'Hyères, ouvre la ligne directe vers Paris.",
      "Hyères est aussi la porte des îles d'Or : nous vous déposons à La Tour Fondue pour la navette de Porquerolles, ou au port pour Port-Cros et Le Levant. Sur place, la mise à disposition garde le même chauffeur pour vos journées, avec des forfaits fixes définis à l'avance et un devis immédiat.",
    ],
    bullets: [
      "Aéroport à 15 min",
      "Dépose embarcadère des îles",
      "Forfaits fixes & devis immédiat",
    ],
  },
  process: {
    heading: "Réserver un chauffeur à Hyères",
    steps: [
      "Indiquez votre vol (Toulon-Hyères ou Nice), votre navette pour les îles ou votre adresse.",
      "Nous conseillons le véhicule selon les bagages, le groupe et la distance.",
      "Le chauffeur suit le vol ou l'horaire du bateau et cale le départ.",
      "Transfert direct ou mise à disposition pour la ville et la presqu'île.",
    ],
  },
  faq: [
    cityPriceFaq("fr", "Hyères"),
    {
      q: "Quel est l'aéroport le plus proche d'Hyères ?",
      a: "L'aéroport de Toulon-Hyères est le plus proche : il se trouve sur la commune, à environ 15 minutes du centre. Nice Côte d'Azur, pour les longs-courriers, est à près de 1h30.",
    },
    {
      q: "Déposez-vous à La Tour Fondue pour Porquerolles ?",
      a: "Oui. Nous vous conduisons à l'embarcadère de La Tour Fondue, sur la presqu'île de Giens, pour la navette de Porquerolles, et au port d'Hyères pour Port-Cros et Le Levant.",
    },
    {
      q: "Assurez-vous les transferts vers la gare TGV de Toulon ?",
      a: "Oui, la gare de Toulon dessert les TGV vers Paris ; nous calons le départ sur votre horaire de train. La gare d'Hyères sert quant à elle les liaisons régionales.",
    },
    {
      q: "Combien de temps à l'avance faut-il réserver ?",
      a: "Anticipez en été et lors des festivals de la Villa Noailles ; nous prenons aussi les demandes de dernière minute selon disponibilité.",
    },
    {
      q: "Vos chauffeurs parlent-ils anglais ?",
      a: "Oui, nos chauffeurs sont bilingues français/anglais, habitués à la clientèle internationale des îles et de la presqu'île.",
    },
    {
      q: "Quels véhicules proposez-vous ?",
      a: "Berline Mercedes Classe E ou Classe S, van premium 7 places pour les groupes et les bagages, SUV et minibus VIP selon l'occasion.",
    },
    {
      q: "Faites-vous les longues distances depuis Hyères ?",
      a: "Oui : Saint-Tropez, Cannes, Nice, Monaco, Marseille et l'Italie, ainsi que la desserte depuis Cannes ou Nice vers Hyères, en véhicule confortable et chauffeur dédié.",
    },
  ],
  relatedLinks: [
    {
      label: "Transferts sur la Côte d’Azur",
      href: "/chauffeur/cote-d-azur",
    },
    { label: "Réserver un chauffeur à Nice", href: "/chauffeur/nice" },
    {
      label: "Voiture avec chauffeur à Saint-Tropez",
      href: "/chauffeur/saint-tropez",
    },
    {
      label: "Voitures à louer à Hyères",
      href: "/location/location-voiture-hyeres",
    },
  ],
  cta: {
    title: "Votre chauffeur privé à Hyères",
    text: "Aéroport à 15 min, départ des îles d'Or ou journée sur la presqu'île : réservez votre chauffeur, 7j/7.",
    primaryHref: "/reservation",
    primaryLabel: "Réserver / demander un devis",
  },
  seo: {
    title: "Chauffeur privé à Hyères — VTC & transfert aéroport",
    description:
      "Chauffeur privé et VTC à Hyères : aéroport de Toulon-Hyères à 15 min, transfert Nice, gare TGV de Toulon, presqu'île de Giens et départ des îles d'Or.",
    canonical: "/chauffeur/hyeres",
  },
};

export default hyeres;
